import { memo, useCallback, useMemo, useState } from "react";

import { CHANGE_COLOR, CHANGE_PREFIX } from "../constants";
import RunCodeChangesExpandableHeader from "../ExpandableHeader";
import { getGroupChangeType, isArrayGroup } from "../helpers";
import RunCodeChangesPrefix from "../Prefix";
import RunCodeChangesRow from "../Row";
import type { RunCodeChangesGroupNode } from "../types";
import styles from "./styles.module.css";

type RunCodeChangesGroupProps = {
  node: RunCodeChangesGroupNode;
};

const RunCodeChangesGroup = memo(({ node }: RunCodeChangesGroupProps) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const isArray = isArrayGroup(node.children);
  const openBracket = isArray ? "[" : "{";
  const closeBracket = isArray ? "]" : "}";

  const groupChangeType = useMemo(() => getGroupChangeType(node.children), [node.children]);
  const prefix = groupChangeType ? (CHANGE_PREFIX[groupChangeType] ?? "\u00a0") : "\u00a0";
  const color = groupChangeType ? CHANGE_COLOR[groupChangeType] : undefined;

  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  return (
    <>
      <RunCodeChangesExpandableHeader isExpanded={isExpanded} onToggle={handleToggle}>
        <RunCodeChangesPrefix prefix={prefix} color={color} />
        <span>
          {node.key}: {openBracket}
        </span>
        {!isExpanded && (
          <>
            <span className={styles.ellipsis}>...</span>
            <span>{closeBracket}</span>
          </>
        )}
      </RunCodeChangesExpandableHeader>
      {isExpanded && (
        <>
          <div className={styles.content}>
            {node.children.map((child) =>
              child.kind === "group" ? (
                <RunCodeChangesGroup key={child.id} node={child} />
              ) : (
                <RunCodeChangesRow key={child.id} name={child.key} item={child.item} />
              )
            )}
          </div>
          <span className={styles.closeBracket}>{closeBracket}</span>
        </>
      )}
    </>
  );
});

RunCodeChangesGroup.displayName = "RunCodeChangesGroup";

export default RunCodeChangesGroup;
